import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  MenuItem,
  Select,
} from "@mui/material";
import { useParams, useLocation } from "react-router-dom";
import axios from "axios";
import NavigationBar from "../../Layout/Header/header";
import Footer from "../../Layout/Footer/footer";
import { AuthContext } from "../../context/authcontext";
import { API_CONFIG } from "../../api/apiUrl";
import toast from "react-hot-toast";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";

import Uploadicon from "../../images/uploadicon.svg";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";


interface AuthContextType {
  userProfile: { user: { firstName: string; login: string } } | null;
  user?: { id: number; login: string; firstName?: string | null };
  loading: boolean;
  authorities: string[];
  plots: { plot: { id: number; plotLocation?: string } }[];
  selectedPlotId: number | null;
}

const statuses = ["PENDING", "In Process", "Complete", "On Hold"];

const UserMileStone = () => {
  const [milestoneStages, setMilestoneStages] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const token = localStorage.getItem("id_token");
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  const authContext = useContext(AuthContext) as AuthContextType | undefined;
  if (!authContext) throw new Error("UserMileStone must be used within an AuthProvider");
  const { loading: authLoading, selectedPlotId, authorities, user } = authContext;

  const queryParams = new URLSearchParams(location.search);
  const plotIdFromQuery = queryParams.get("plotId");
  const selectedPlotIdFinal = plotIdFromQuery ? Number(plotIdFromQuery) : selectedPlotId;

  const isUser = authorities.includes("ROLE_USER");

  const getStatus = (remarks?: string) => remarks || "";

  const fetchMilestoneStages = async () => {
    if (!id || !selectedPlotIdFinal) {
      toast.error("Missing company ID or plot ID", { id: "params-error" });
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const companyId = isUser ? 0 : id ? parseInt(id) : 1;
      const response = await axios.get(
        API_CONFIG.COMPANY_PLOT_CONSTRUCTION_STAGES(companyId, selectedPlotIdFinal, 0, 20),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      const apiData = response.data.constructionStages || [];
      setMilestoneStages(apiData);
      
    } catch (error: any) {
      toast.error("Failed to fetch milestone stages: " + (error.response?.data?.detail || error.message), {
        id: "fetch-stages-error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if ((isUser || id) && selectedPlotIdFinal) {
      fetchMilestoneStages();
    }
  }, [id, selectedPlotIdFinal, isUser]);

  const [openModal, setOpenModal] = useState(false);
  const [selectedStage, setSelectedStage] = useState<any>(null);
  const [bookingAmount, setBookingAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [paymentReferenceId, setPaymentReferenceId] = useState("");
  const [files, setFiles] = useState<any[]>([]);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleOpenModal = (stage: any) => {
    setSelectedStage(stage);
    setBookingAmount(stage.amount?.toString() || "0");
    setOpenModal(true);
  };
  const handleCloseModal = () => {
    setOpenModal(false);
    setBookingAmount("");
    setPaymentDate("");
    setPaymentMode("");
    setPaymentReferenceId("");
    setSelectedStage(null);
    setFiles([]);
    setUploadSuccess(false);
  };
  const uploadFile = async (file: File): Promise<string | null> => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axios.post<string>(
        API_CONFIG.FILE_UPLOAD,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      toast.error("Failed to upload file");
      return null;
    }
  };
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const newFiles = Array.from(e.target.files);
    setIsUploading(true);
    const uploadedFiles: { file: File; url: string; previewUrl: string }[] = [];
    for (const file of newFiles) {
      if (file.size <= 10 * 1024 * 1024) {
        const uploadedUrl = await uploadFile(file);
        if (uploadedUrl) {
          uploadedFiles.push({
            file,
            url: uploadedUrl,
            previewUrl: URL.createObjectURL(file),
          });
        }
      } else {
        toast.error(`File ${file.name} exceeds the 10MB limit`);
      }
    }
    setFiles((prev: any[]) => [...prev, ...uploadedFiles]);
    if (uploadedFiles.length > 0) {
      setUploadSuccess(true);
      toast.success("Image uploaded successfully!");
      setTimeout(() => setUploadSuccess(false), 3000);
    }
    setIsUploading(false);
  };
  const removeFile = (index: number) => {
    setFiles((prev: any[]) => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].previewUrl);
      newFiles.splice(index, 1);
      return newFiles;
    });
    toast.success("File removed");
  };
  const handleSavePayment = async () => {
    if (!bookingAmount || !paymentDate || !paymentMode || !selectedStage) {
      toast.error("Missing payment details");
      return;
    }
    if (files.length === 0) {
      toast.error("Upload Payment Proof is required");
      return;
    }
    const amount = parseFloat(bookingAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Invalid payment amount");
      return;
    }
    const imageUrl = files.length > 0 ? files[0].url : null;
    const postData = {
      paymentAmount: amount,
      paymentMode,
      paymentRefernce: paymentReferenceId,
      craetedDate: new Date().toISOString(),
      remarks: "Payment",
      description: "Payment for stage",
      imgUrl: imageUrl,
      status: "SUBMITTED",
      paymentDate: paymentDate ? paymentDate + "T00:00:00Z" : null,
      companyPlotConstructionStages: { id: selectedStage.id },
      user: user ? {
        id: user.id,
        login: user.login,
        firstName: user.firstName || "Unknown",
      } : undefined,
    };
    try {
      await axios.post(API_CONFIG.PAYMENT_URL, postData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      toast.success("Payment saved successfully");
      handleCloseModal();
      fetchMilestoneStages();
    } catch (error) {
      toast.error("Failed to save payment");
    }
  };

  return (
    <Box sx={{ maxWidth: "2000px", margin: "0 auto" }}>
      <NavigationBar />
      <Box
        sx={{
          py: { xs: 2, sm: 3, md: 3, lg: 3, xl: 3 },
          px: { xs: 1, sm: 0.8, md: 2, lg: 4, xl: 6 },
        }}
      >
        <Box sx={styles.mainContainer}>
          <Box sx={styles.header}>
            <Typography
              sx={{
                fontSize: { xs: "20px", sm: "25px", md: "25px" },
                fontWeight: 500,
                lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 },
              }}
            >
              Milestone
            </Typography>
          </Box>

          {(loading || authLoading) ? (
            <div className="loader-container">
                <div className="spinner"></div>
                <p>Loading, please wait...</p>
            </div>
          ) : milestoneStages.length > 0 ? (
            <Box sx={styles.mainBox}>
              <Box sx={styles.borderBox}>
                <Box sx={{ padding: { xs: 0.5, sm: 2, md: 1 } }}>
                  <Box sx={{ maxWidth: "700px" }}>
                    <Box
                      sx={{
                        pt: { xs: 1, md: 2.7 },
                        px: { xs: 2, sm: 1, md: 2, lg: 7, xl: 10 },
                      }}
                    >
                      <Grid container spacing={1}>
                        {milestoneStages[0]?.plot?.plotOwner?.user?.firstName && (
                          <>
                            <Grid item xs={6}>
                              <Typography sx={styles.fonstyle}>Customer Name</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography sx={styles.fonstyle}>
                                {milestoneStages[0].plot.plotOwner.user.firstName}
                              </Typography>
                            </Grid>
                          </>
                        )}
                        {milestoneStages[0]?.plot?.plotLocation && (
                          <>
                            <Grid item xs={6}>
                              <Typography sx={styles.fonstyle}>Plot Location</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography sx={styles.fonstyle}>
                                {milestoneStages[0].plot.plotLocation}
                              </Typography>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Box>
                  </Box>
                  <Box sx={{ maxWidth: "900px" }}>
                    <Box
                      sx={{
                        pt: { xs: 1, md: 1 },
                        px: { xs: 1.4, sm: 1, md: 2, lg: 7, xl: 10 },
                      }}
                    >
                      {milestoneStages.map((stage, index) => {
                        const stageName = stage.constructionStages?.name || stage.description || "Unnamed Stage";
                        const status = getStatus(stage.remarks);
                        const isBooking = stageName.toLowerCase() === "booking";
                        const isPaid = !!stage.isPaid;
                        const showSingleDate = isBooking && stage.startDate && stage.endDate && stage.startDate === stage.endDate;

                        return (
                          <Grid
                            container
                            alignItems="center"
                            spacing={1}
                            sx={{
                              mb: 2,
                              // No special background, border, or boxShadow for Booking paid row
                            }}
                            key={stage.id || index}
                          >
                            <Grid item xs={12} sm={4}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={styles.fonstyle}>
                                  {stageName}
                                </Typography>
                                {stage.isPaid && (
                                  <Tooltip
                                    title={
                                      <Box>
                                        <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 0.5 }}>Payment Completed</Typography>
                                        {stage.paymentDate && (
                                          <Typography sx={{ fontSize: 13 }}>Date: {stage.paymentDate.slice(0, 10)}</Typography>
                                        )}
                                        <Typography sx={{ fontSize: 13 }}>Amount: ₹{stage.amount || 'N/A'}</Typography>
                                      </Box>
                                    }
                                    arrow
                                    placement="top"
                                  >
                                    <IconButton size="small" sx={{ p: 0.5, color: '#43a047' }}>
                                      <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </Grid>
                            <Grid item xs={6} sm={2.5} md={2.5}>
                              {isBooking && isPaid && stage.paymentDate ? (
                                <input
                                  type="date"
                                  value={stage.paymentDate.slice(0, 10) || ""}
                                  disabled
                                  style={styles.dateInput}
                                />
                              ) : (
                                <input
                                  type="date"
                                  value={stage.startDate || ""}
                                  disabled={true}
                                  onChange={(e) => setMilestoneStages((prev) => prev.map((s, i) => i === index ? { ...s, startDate: e.target.value } : s))}
                                  style={styles.dateInput}
                                />
                              )}
                            </Grid>
                            <Grid item xs={6} sm={2.5} md={2.5}>
                              {isBooking && isPaid && stage.paymentDate ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', pl: 1 }}>
                                  {/* <Typography sx={{ fontWeight: 600, fontSize: 12, color: '#43a047', mb: 0.5 }}>
                                    Payment Date:
                                  </Typography>
                                  <Typography sx={{ fontWeight: 500, fontSize: 14, color: '#333' }}>
                                    {stage.paymentDate.slice(0, 10)}
                                  </Typography> */}
                                </Box>
                              ) : showSingleDate && isBooking && isPaid ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pl: 1 }}>
                                  <Chip
                                    label="Paid"
                                    color="success"
                                    icon={<CheckCircleIcon />}
                                    sx={{ fontWeight: 600, fontSize: 14, bgcolor: '#43a047', color: 'white', px: 1.5 }}
                                  />
                                </Box>
                              ) : (
                                <input
                                  type="date"
                                  value={stage.endDate || ""}
                                  disabled={true}
                                  onChange={(e) => setMilestoneStages((prev) => prev.map((s, i) => i === index ? { ...s, endDate: e.target.value } : s))}
                                  style={styles.dateInput}
                                />
                              )}
                            </Grid>
                            <Grid item xs={6} sm={2} md={2}>
                              {/* Hide status dropdown for Booking paid */}
                              {isBooking && isPaid ? null : (
                                <Select
                                  fullWidth
                                  size="small"
                                  value={status}
                                  disabled={true}
                                  displayEmpty
                                  sx={styles.select}
                                >
                                  <MenuItem value="" disabled>
                                    Status
                                  </MenuItem>
                                  <MenuItem value={status}>
                                    {status}
                                  </MenuItem>
                                </Select>
                              )}
                            </Grid>
                            <Grid item xs={6} sm={1} md={1} lg={1}>
                              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                {/* Show Pay button when isPaymentRequested is true and isPaid is false */}
                                {stage.isPaymentRequested && !stage.isPaid ? (
                                  <Button
                                    size="small"
                                    sx={{
                                      bgcolor: '#89652A',
                                      color: 'white',
                                      '&:hover': { bgcolor: '#6b4e1f' },
                                      fontSize: { xs: '11px', sm: '11px', md: '12px', lg: '13px' },
                                      height: '38px',
                                      minWidth: '70px',
                                      borderRadius: '10px',
                                      textTransform: 'none',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontWeight: 500,
                                      transition: 'all 0.2s ease',
                                      marginLeft: '10px',
                                    }}
                                    onClick={() => handleOpenModal(stage)}
                                  >
                                    Pay
                                  </Button>
                                ) : !stage.isPaymentRequested ? (

                                  <Button
                                    size="small"
                                    sx={{
                                      bgcolor: '#ccc',
                                      color: '#666',
                                      fontSize: { xs: '11px', sm: '11px', md: '12px', lg: '13px' },
                                      height: '38px',
                                      minWidth: '70px',
                                      borderRadius: '10px',
                                      textTransform: 'none',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontWeight: 500,
                                      transition: 'all 0.2s ease',
                                      marginLeft: '10px',
                                      cursor: 'not-allowed',
                                    }}
                                    disabled={true}
                                  >
                                    Pay
                                  </Button>
                                ) : null}
                              </Box>
                            </Grid>
                          </Grid>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 220,
                width: '100%',
 
              }}
            >
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '10px',
                  background: '#fafafa',
                  p: 4,
                  textAlign: 'center',
                  maxWidth: 500,
                  width: '100%',
                }}
              >
                <Typography variant="h6" sx={{ color: '#B69D74', fontWeight: 600, mb: 1 }}>
                  No Milestones Found
                </Typography>
                <Typography sx={{ color: '#666', fontSize: '16px' }}>
                  There are currently no milestones available for this plot.<br />
        
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      <Footer />
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="payment-modal-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "80%", sm: 400, md: 600 },
            maxHeight: "80vh",
            overflowY: "auto",
            my: 2,
            bgcolor: "white",
            borderRadius: "10px",
            boxShadow: 24,
            p: 4,
            outline: "none",
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <Typography
                id="payment-modal-title"
                variant="h6"
                component="h2"
                sx={{ ...modalTitleStyle, color: "#111" }}
              >
                Payment Details
              </Typography>
              <IconButton
                onClick={handleCloseModal}
                sx={{
                  bgcolor: "#f4f4f4",
                  color: "#89652A",
                  "&:hover": { bgcolor: "#f4f4f4" },
                }}
              >
                <CloseIcon sx={{ color: "#89652A" }} />
              </IconButton>
            </Box>
           
          </Box>
          <Box component="form" sx={{ width: "100%" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Typography sx={labelStyle}>Amount</Typography>
                <TextField
                  variant="outlined"
                  placeholder="Enter amount"
                  size="medium"
                  type="number"
                  value={bookingAmount}
                  onChange={(e) => setBookingAmount(e.target.value)}
                  fullWidth
                  sx={contactTextField}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Typography sx={labelStyle}>Payment Date</Typography>
                <TextField
                  variant="outlined"
                  size="medium"
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={contactTextField}
                  inputProps={{ max: new Date().toISOString().split("T")[0] }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Typography sx={labelStyle}>Select Payment Mode</Typography>
                <TextField
                  select
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                  variant="outlined"
                  size="medium"
                  fullWidth
                  sx={contactTextField}
                >
                  <MenuItem value="" disabled>
                    Payment Mode
                  </MenuItem>
                  <MenuItem value="UPI">UPI</MenuItem>
                  <MenuItem value="Cash">Cash</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Typography sx={labelStyle}>Payment Reference ID</Typography>
                <TextField
                  size="medium"
                  variant="outlined"
                  placeholder="Payment Reference ID"
                  value={paymentReferenceId}
                  onChange={(e) => setPaymentReferenceId(e.target.value)}
                  fullWidth
                  sx={contactTextField}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={8} lg={8} sx={{ mx: "auto" }}>
                <Typography sx={labelStyle}>Upload Payment Proof</Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleFileChange}
                  />
                  <Box
                    onClick={() => fileInputRef.current?.click()}
                    sx={uploadBoxDetailsAddStyleFullWidth}
                  >
                    <img
                      src={Uploadicon}
                      alt="Upload"
                      style={{ height: "44px", margin: "8px 0" }}
                    />
                    <Typography
                      sx={{
                        fontSize: "15px",
                        fontWeight: 500,
                        color: "#89652A",
                        mb: 0.5,
                      }}
                    >
                      Browse File
                    </Typography>
                    <Typography sx={{ fontSize: "13px", color: "#666" }}>
                      Max 10 MB (.jpg, .png, .pdf, .jpeg)
                    </Typography>
                    {uploadSuccess && (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="File uploaded successfully!"
                        color="success"
                        variant="outlined"
                        sx={{ mt: 1, mx: "auto" }}
                      />
                    )}
                  </Box>
                  {files.length > 0 && (
                    <Box sx={styles.imageGridContainer}>
                      {files.map((fileObj, index) => (
                        <Box key={index} sx={styles.imageGridItem}>
                          <img
                            src={fileObj.previewUrl}
                            alt={`Uploaded ${index}`}
                            style={styles.gridImage}
                          />
                          <IconButton
                            sx={styles.removeImageButton}
                            onClick={() => removeFile(index)}
                          >
                            <CloseIcon sx={{ fontSize: 18, color: "white" }} />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sx={{ textAlign: "center" }}>
                <Button
                  variant="contained"
                  onClick={handleSavePayment}
                  disabled={
                    isUploading ||
                    !bookingAmount ||
                    !paymentDate ||
                    !paymentMode ||
                    !paymentReferenceId ||
                    isNaN(parseFloat(bookingAmount)) ||
                    parseFloat(bookingAmount) <= 0 ||
                    files.length === 0
                  }
                  sx={contactSubmitButton}
                  size="large"
                >
                  {isUploading ? (
                    <CircularProgress size={24} sx={{ color: "white" }} />
                  ) : (
                    "Save Payment"
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default UserMileStone;

const styles: any = {
  mainContainer: {
    borderRadius: { xs: "10px", sm: "10px" },
    boxShadow: "0px 0px 3px 0px #00000033",
  },
  header: {
    background: "linear-gradient(90deg, #89652A 0%, #B69D74 100%)",
    color: "#fff",
    padding: { xs: "10px 12px", sm: "14px 20px", md: "16px 24px" },
    borderTopLeftRadius: { xs: "6px", sm: "8px" },
    borderTopRightRadius: { xs: "6px", sm: "8px" },
  },
  mainBox: {
    mt: 3,
    px: { xs: "13px", sm: "20px", md: "30px", lg: "50px" },
    pb: { xs: "13px", sm: "15px", md: "20px", lg: "20px" },
  },
  borderBox: {
    borderRadius: "10px",
    border: "1px solid #2D2D2D33",
  },
  fonstyle: {
    fontSize: { xs: "14px", sm: "15px", md: "15px", lg: "16px", xl: "16px" },
    fontWeight: { xs: 500, sm: 500, md: 500, lg: 500 },
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
      textTransform: "capitalize",
  
  },
  select: {
    borderRadius: "50px",
    "& .MuiOutlinedInput-notchedOutline": { border: "1px solid black" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "black" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "black" },
    "& .MuiSelect-select": { paddingY: "7px", fontSize: "16px", color: "#000" },
  },
  dateInput: {
    width: "100%",
    padding: "7px 10px",
    borderRadius: "50px",
    border: "1px solid #ccc",
    fontSize: "16px",
    color: "#666",
    backgroundColor: "#f5f5f5",
    outline: "none",
    boxSizing: "border-box",
  },
  imageGridContainer: {
    display: "grid",
    gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" },
    gap: 2,
    mb: 3,
  },
  imageGridItem: {
    position: "relative",
    borderRadius: "12px",
    overflow: "hidden",
    aspectRatio: "1/1",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
  },
  gridImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  removeImageButton: {
    position: "absolute",
    top: 8,
    right: 8,
    bgcolor: "rgba(0,0,0,0.6)",
    width: 28,
    height: 28,
    "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
  },
};

const contactTextField = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    borderColor: "#89652A",
    bgcolor: "#f5f5f5",
    minHeight: 38,
    fontSize: "14px",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#B69D74",
      borderWidth: "1.2px",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#89652A" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#89652A",
      borderWidth: "1.5px",
    },
  },
  "& .MuiInputBase-input": { padding: "10px 12px", fontSize: "14px" },
};
const contactSubmitButton = {
  textTransform: "capitalize",
  fontSize: { xs: "14px", md: "14px" },
  fontWeight: 600,
  borderRadius: "22px",
  color: "white",
  bgcolor: "#89652A",
  py: 0.5,
  px: { xs: 3, md: 4 },
  mt: 1.2,
  "&:hover": { bgcolor: "#6b4e20" },
  "&:disabled": { bgcolor: "#a88b5e" },
};
const uploadBoxDetailsAddStyleFullWidth = {
  border: "2px dashed #B69D74",
  borderRadius: "10px",
  p: 1.2,
  textAlign: "center",
  mb: 1.2,
  cursor: "pointer",
  bgcolor: "rgba(182, 157, 116, 0.07)",
  width: "100%",
  maxWidth: "100%",
  transition: "border-color 0.2s",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  "&:hover": { borderColor: "#89652A" },
};
const modalTitleStyle = {
  fontSize: { xs: "17px", sm: "20px", md: "20px" },
  fontWeight: 500,
  lineHeight: 1.3,
  color: "#89652A",
  fontFamily: "Poppins, sans-serif",
};

const labelStyle = {
  fontSize: { xs: "13px", sm: "14px", md: "14px", lg: "15px", xl: "15px" },
  fontWeight: 500,
  color: "#2D2D2D",
  fontFamily: "Poppins, sans-serif",
  mb: 0.7,
};
